import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../index";
import type { PoolTournamentState } from "@/shared/types";

export enum PoolSessionState {
  LOBBY = "LOBBY",
  WAITING_FOR_START = "WAITING_FOR_START",
  WAITING_FOR_MATCH = "WAITING_FOR_MATCH",
  IN_MATCH = "IN_MATCH",
  TOURNAMENT_COMPLETE = "TOURNAMENT_COMPLETE",
  TOURNAMENT_CANCELLED = "TOURNAMENT_CANCELLED"
}

interface PoolSessionStoreState {
  currentState: PoolSessionState;
  currentUserId: number | null;
  tournamentId: string | null;
  activeMatchRoomId: string | null;
  championUserId: number | null;
  cancelReason: string | null;
  lastTournamentVersion: number | null;
  error: string | null;
  lastTransitionAt: number;
  transitionHistory: Array<{ from: PoolSessionState; to: PoolSessionState; at: number }>;
}

const initialState: PoolSessionStoreState = {
  currentState: PoolSessionState.LOBBY,
  currentUserId: null,
  tournamentId: null,
  activeMatchRoomId: null,
  championUserId: null,
  cancelReason: null,
  lastTournamentVersion: null,
  error: null,
  lastTransitionAt: Date.now(),
  transitionHistory: []
};

function transition(state: PoolSessionStoreState, targetState: PoolSessionState): void {
  if (state.currentState === targetState) {
    return;
  }

  state.transitionHistory.push({
    from: state.currentState,
    to: targetState,
    at: Date.now()
  });
  if (state.transitionHistory.length > 20) {
    state.transitionHistory.shift();
  }
  state.currentState = targetState;
  state.lastTransitionAt = Date.now();
}

const poolSessionSlice = createSlice({
  name: "poolSession",
  initialState,
  reducers: {
    setPoolSessionUserId: (state, action: PayloadAction<number | null>) => {
      state.currentUserId = action.payload;
    },
    poolSessionJoined: (state, action: PayloadAction<string>) => {
      state.tournamentId = action.payload;
      state.activeMatchRoomId = null;
      state.championUserId = null;
      state.cancelReason = null;
      state.error = null;
      transition(state, PoolSessionState.WAITING_FOR_START);
    },
    poolTournamentLifecycleUpdated: (state, action: PayloadAction<PoolTournamentState>) => {
      const payload = action.payload;
      state.tournamentId = payload.tournamentId;
      state.lastTournamentVersion = payload.version;
      state.error = null;
      state.cancelReason = payload.cancelReason || null;

      if (payload.status === "cancelled") {
        state.activeMatchRoomId = null;
        transition(state, PoolSessionState.TOURNAMENT_CANCELLED);
        return;
      }

      if (payload.status === "completed") {
        state.activeMatchRoomId = null;
        state.championUserId = payload.championUserId || null;
        transition(state, PoolSessionState.TOURNAMENT_COMPLETE);
        return;
      }

      const myActiveMatch =
        state.currentUserId === null
          ? null
          : payload.matches.find(
              (match) =>
                match.round === payload.round &&
                match.status === "active" &&
                (match.playerAUserId === state.currentUserId || match.playerBUserId === state.currentUserId)
            ) || null;

      if (myActiveMatch?.roomId) {
        state.activeMatchRoomId = myActiveMatch.roomId;
        transition(state, PoolSessionState.IN_MATCH);
        return;
      }

      state.activeMatchRoomId = null;
      if (payload.status === "waiting") {
        transition(state, PoolSessionState.WAITING_FOR_START);
      } else {
        transition(state, PoolSessionState.WAITING_FOR_MATCH);
      }
    },
    poolSessionCommandRejected: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    resetPoolSession: () => initialState
  }
});

export const {
  setPoolSessionUserId,
  poolSessionJoined,
  poolTournamentLifecycleUpdated,
  poolSessionCommandRejected,
  resetPoolSession
} = poolSessionSlice.actions;

export const selectPoolSessionState = (state: RootState) => state.poolSession.currentState;
export const selectPoolSessionError = (state: RootState) => state.poolSession.error;
export const selectPoolSessionActiveMatchRoomId = (state: RootState) => state.poolSession.activeMatchRoomId;
export const selectPoolSessionTournamentId = (state: RootState) => state.poolSession.tournamentId;

export default poolSessionSlice.reducer;
